/**
 * Step 3 — Verify ID (CIP).
 *
 * Per `rcp-apply-now-step3-cip-id.html`. Uploads the ID image and shows
 * OCR confidence scores.
 *
 * CRITICAL a11y fix (mockup #3 launch blocker):
 *   Confidence pills are NOT color-only. Each pill has:
 *     - text label ("High confidence" / "Needs review")
 *     - icon shape (CheckCircle for high, AlertTriangle for low)
 *     - aria-label combining score + verdict
 *     - role="status" so SR reads it
 *
 * Per rcp-security §B.5 #2.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useApplyWizard } from './ApplyWizard';
import { useToast } from '../../components/a11y/AccessibleToast';
import { getApiClient } from '../../services/apiClient';
import { DropZone } from '../../components/a11y/DropZone';
import { ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle } from '@retail-ops/panel-shell/fi';

interface ExtractedField {
  key: string;
  label: string;
  value: string;
  confidence: number;
}

export function Step3CipId(): JSX.Element {
  const { gotoStep } = useApplyWizard();
  const { pushToast } = useToast();
  const navigate = useNavigate();
  const [uploaded, setUploaded] = useState<string | null>(null);
  const [extracted, setExtracted] = useState<ReadonlyArray<ExtractedField>>([]);
  const [announcing, setAnnouncing] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function onFile(file: File): Promise<void> {
    setSubmitting(true);
    try {
      const b64 = await readAsBase64(file);
      const client = getApiClient();
      // TODO(PLANNED /portal/documents/upload): server runs OCR + returns extracted fields.
      const res = await client.portal.documents.upload({
        filename: file.name,
        contentType: file.type,
        base64Contents: b64,
        category: 'id-document',
      });
      if (res.success && res.data) {
        setUploaded(res.data.filename);
        // Placeholder extracted — real server returns these with the upload response.
        const mocked: Array<ExtractedField> = [
          { key: 'name', label: 'Full name', value: 'Pending review', confidence: 0.94 },
          { key: 'dob', label: 'Date of birth', value: 'Pending review', confidence: 0.87 },
          { key: 'idNumber', label: 'ID number', value: 'Pending review', confidence: 0.72 },
          { key: 'expireDate', label: 'Expires', value: 'Pending review', confidence: 0.65 },
        ];
        setExtracted(mocked);
        pushToast('success', 'ID received', 'We pulled the details below. Check and correct anything that looks wrong.');
      } else {
        pushToast('error', 'Upload failed', res.error ?? 'Please try a different image.');
      }
    } catch {
      pushToast('error', 'Upload failed', 'Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function continueNext(): void {
    if (!uploaded) {
      pushToast('error', 'Please upload your ID before continuing');
      return;
    }
    navigate('/apply/disclosures');
  }

  return (
    <section aria-labelledby="step3-heading">
      <h1 id="step3-heading" tabIndex={-1}>Verify your identity</h1>
      <p className="lead" style={{ marginBottom: 24 }}>
        Upload a clear photo of your driver&apos;s license or state-issued ID. We&apos;ll pull
        the details so you don&apos;t have to retype them.
      </p>

      {!uploaded ? (
        <div className="card">
          <DropZone
            label="Upload ID (front)"
            onFile={(f) => void onFile(f)}
            onAnnounce={(msg) => setAnnouncing(msg)}
            onError={(msg) => pushToast('error', 'Upload rejected', msg)}
          />
          <div aria-live="polite" className="visually-hidden">{announcing}</div>
        </div>
      ) : (
        <div className="card">
          <h2>What we pulled from your ID</h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {extracted.map((f) => (
              <ConfidenceRow key={f.key} field={f} />
            ))}
          </ul>
          <button type="button" className="btn btn-ghost" onClick={() => setUploaded(null)}>
            Replace image
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', marginTop: 16 }}>
        <button type="button" className="btn btn-ghost" onClick={() => gotoStep(1)}>
          <ArrowLeft aria-hidden="true" size={16} /> Back
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={continueNext}
          disabled={submitting || !uploaded}
          aria-busy={submitting || undefined}
        >
          Continue
          <ArrowRight aria-hidden="true" size={16} />
        </button>
      </div>
    </section>
  );
}

function ConfidenceRow({ field }: { field: ExtractedField }): JSX.Element {
  const isHigh = field.confidence >= 0.80;
  const verdict = isHigh ? 'High confidence' : 'Needs review';
  const Icon = isHigh ? CheckCircle2 : AlertTriangle;
  const pctLabel = `${Math.round(field.confidence * 100)}%`;
  const bg = isHigh ? 'var(--ok-bg)' : 'var(--warn-bg)';
  const fg = isHigh ? 'var(--ok-text)' : 'var(--warn-text)';
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        padding: '12px 0',
        borderBottom: '1px solid var(--surface-border)',
      }}
    >
      <div>
        <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>{field.label}</div>
        <div style={{ fontWeight: 500, color: 'var(--pcb-blue-dark)' }}>{field.value}</div>
      </div>
      <span
        role="status"
        aria-label={`${field.label}, ${pctLabel} confidence, ${verdict}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: bg,
          color: fg,
          padding: '4px 10px',
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 500,
        }}
      >
        <Icon aria-hidden="true" size={14} />
        {verdict} &middot; {pctLabel}
      </span>
    </li>
  );
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const base64 = result.slice(result.indexOf(',') + 1);
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}
